// ============================================================
// SkillRows - the skills the sheet is trained in, one row each.
//
// A row is the skill, the characteristic it runs on, how far it is
// trained, a free modifier, and the number it tests at. Only trained
// skills are listed (a marine has six of thirty-four); ALL shows the
// whole catalog, which is also how an untrained one gets trained.
// ============================================================
import { useState } from 'react';
import type { Character, SkillId } from '../../engine/character/types';
import { SKILL_CATALOG, type SkillDefinition } from '../../engine/character/skillCatalog';
import { getSkillRating } from '../../engine/character/normalize';
import { collectSkillBonuses } from '../../engine/character/effectiveTalents';
import { TIER_LABELS, skillPatch, skillTotal, tierLevel, withTierLevel } from '../../engine/character/skillTiers';
import { CHARACTERISTIC_TEST_LABELS } from '../../engine/characteristicTests';
import ReadoutNumber from './ReadoutNumber';
import styles from './Sheet.module.css';

interface Props {
  character: Character;
  onChange: (partial: Partial<Character>) => void;
  disabled: boolean;
}

/** The trained skills, in catalog order. */
function trainedSkills(character: Character): SkillDefinition[] {
  return SKILL_CATALOG.filter((def) => getSkillRating(character, def.id).trained);
}

/** "6 trained of 34" for the caption. */
export function skillSummary(character: Character): string {
  return `${trainedSkills(character).length} trained of ${SKILL_CATALOG.length}`;
}

interface RowProps extends Props {
  def: SkillDefinition;
}

function SkillRow({ def, character, onChange, disabled }: RowProps) {
  const rating = getSkillRating(character, def.id);
  const talents = collectSkillBonuses(character, def.id);
  const talentBonus = talents.reduce((sum, bonus) => sum + bonus.value, 0);
  const total = skillTotal(rating, character.characteristics[def.governing].value, talentBonus, def.advanced);
  const set = (change: Parameters<typeof skillPatch>[2]) => onChange(skillPatch(character, def.id as SkillId, change));
  return (
    <div className={styles.skillRow} data-testid={`skill-row-${def.id}`}>
      <span className={styles.skillName} title={def.advanced ? 'Advanced: cannot be tested untrained' : undefined}>
        {def.name}
        {def.advanced && <i className={styles.chipTag}>adv</i>}
      </span>
      <span className={styles.skillChar}>{CHARACTERISTIC_TEST_LABELS[def.governing]}</span>
      <select
        className={styles.select}
        aria-label={`${def.name} training`}
        value={tierLevel(rating)}
        disabled={disabled}
        onChange={(e) => set((r) => withTierLevel(r, Number(e.target.value)))}
      >
        {TIER_LABELS.map((label, level) => (
          <option key={label} value={level}>
            {label}
          </option>
        ))}
      </select>
      <ReadoutNumber
        ariaLabel={`${def.name} modifier`}
        size="small"
        allowNegative
        value={rating.mod || 0}
        disabled={disabled}
        title={talents.map((b) => `${b.label} +${b.value}`).join(', ') || 'Modifier'}
        onChange={(mod) => set((r) => ({ ...r, mod }))}
      />
      <span className={styles.skillTotal} data-testid={`skill-total-${def.id}`}>
        {total}
      </span>
    </div>
  );
}

export default function SkillRows({ character, onChange, disabled }: Props) {
  const [all, setAll] = useState(false);
  const shown = all ? SKILL_CATALOG : trainedSkills(character);
  return (
    <>
      {shown.length === 0 && <div className={styles.sectionNote}>No trained skills. ALL lists the catalog.</div>}
      {shown.map((def) => (
        <SkillRow key={def.id} def={def} character={character} onChange={onChange} disabled={disabled} />
      ))}
      <button
        type="button"
        className={styles.addButton}
        data-testid="skills-all"
        aria-pressed={all}
        onClick={() => setAll(!all)}
      >
        {all ? 'TRAINED ONLY' : `ALL ${SKILL_CATALOG.length}`}
      </button>
      <div className={styles.sectionNote}>
        Columns: characteristic, training, modifier, the number it tests at. Untrained basic skills test at half.
      </div>
    </>
  );
}
